import { useEffect, useMemo, useState } from "react";

import { Box, CircularProgress, colors, Fade, MenuItem, Select, Tooltip, Typography, useTheme } from "@mui/material";
import { DataGrid, GridSortModel } from "@mui/x-data-grid";

import { UserAnalyticsService } from "@analytics/index";

import ResourcesLink from "@components/ResourceLink";

import C from "@constants/constants";

import {
  DataSourceType,
  IDataSources,
  IDataSourcesBase,
  IGrantsData,
  ILicensingDealsData,
  ISecFilingsData,
  ISponsoredResearchData,
} from "@interfaces/company.type";

import { useDataSourcesQuery } from "@queries/company.query";

import { getRowClassName, tableStyles } from "../Table/tableStyles";

import { getSortModel, grantsDataColumns, licensingColumns, secColumns, sponsoredColumns } from "./SourcesTableColumns";
import openTextRowPopup from "./utilities/openTextRowPopup";

interface ISourcesProperties {
  id: string;
}

export default function Sources({ id }: ISourcesProperties) {
  // TODO: Delete all these useStates
  const [sponsoredResearchData, setSponsoredResearchData] = useState<ISponsoredResearchData[]>();
  const [secFilingsData, setSecFilingsData] = useState<ISecFilingsData[]>([]);
  const [licensingDealsData, setLicensingDealsData] = useState<ILicensingDealsData[]>();
  const [grantsData, setGrantsData] = useState<IGrantsData[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [selectedSource, setSelectedSource] = useState<DataSourceType | undefined>();
  const theme = useTheme();

  const { data, status } = useDataSourcesQuery(id);

  useEffect(() => {
    if (!data) return;

    const source = Object.keys(data).find((key) => data[key as keyof IDataSources].length > 0) as
      | DataSourceType
      | undefined;
    setSelectedSource(source);
    UserAnalyticsService.trackDataSourceLoaded({ id, source }, "Viewed Sources");

    setSponsoredResearchData(data.sponsoredResearch);
    setSecFilingsData(data.secFilings);
    setLicensingDealsData(data.licensingDeals);
    setGrantsData(data.grants);
    setSortModel(getSortModel(source));
  }, [data, id]);

  const dataSources = useMemo(
    () => ({
      grants: {
        columns: grantsDataColumns(openTextRowPopup),
        data: grantsData,
        description: "SBIR/STTR grant data.",
        title: "Grants",
      },
      licensingDeals: {
        columns: licensingColumns,
        data: licensingDealsData,
        description: "Government and private sector licensing deals.",
        title: "Licensing Deals",
      },
      secFilings: {
        columns: secColumns,
        data: secFilingsData,
        description: "Publicly available SEC Filings that contain patent licenses.",
        title: "SEC Filings",
      },
      sponsoredResearch: {
        columns: sponsoredColumns,
        data: sponsoredResearchData,
        description: "Sponsored Research disclosed by Universities.",
        title: "Partnerships",
      },
    }),
    [sponsoredResearchData, secFilingsData, licensingDealsData, grantsData],
  );

  const handleSelectSource = (source: DataSourceType) => {
    setSelectedSource(source);
    setSortModel(getSortModel(source));
    UserAnalyticsService.trackDataSourceLoaded({ id, source }, "Viewed Sources");
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel[0] && sortModel[0].field && sortModel[0].sort) {
      setSortModel(sortModel);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          backgroundColor: theme.palette.grey[900],
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "space-between",
          px: 3,
          py: 1,
          width: "100%",
        }}
      >
        <div>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <Typography sx={{ color: "white" }} variant="h5">
              Data Sources
            </Typography>
            <ResourcesLink
              color={colors.blue[200]}
              resourceLink={C.LOOM_DATA_RESOURCES_SHARE}
              workflow="Data Sources Tab"
            />
          </Box>
        </div>
        <Select
          displayEmpty
          onChange={(event) => handleSelectSource(event.target.value as DataSourceType)}
          size="small"
          sx={{ backgroundColor: "white", minWidth: 200, my: 2 }}
          value={selectedSource || ""}
        >
          {Object.keys(dataSources).map((key) => (
            <MenuItem disabled={dataSources[key as DataSourceType]?.data?.length === 0} key={key} value={key}>
              <Tooltip title={dataSources[key as DataSourceType].description}>
                <>{dataSources[key as DataSourceType].title}</>
              </Tooltip>
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Fade in={status === "success"}>
        <Box
          sx={{
            flexGrow: 1,
            overflowX: "auto",
            width: "100%",
          }}
        >
          {selectedSource && (
            <DataGrid
              checkboxSelection={false}
              className="sources-height"
              columns={dataSources[selectedSource].columns}
              disableRowSelectionOnClick
              getRowClassName={getRowClassName}
              onSortModelChange={handleSortModelChange}
              rows={dataSources[selectedSource].data as IDataSourcesBase[]}
              sortingOrder={["desc", "asc"]}
              sortModel={sortModel}
              sx={{
                ...tableStyles.root,
              }}
            />
          )}
        </Box>
      </Fade>
      {status === "pending" && (
        <Box
          sx={{
            display: "flex",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}
